import {
  isControllerSpec,
  resolveControllerLayout,
  resolvePlacement,
  validGraphic,
  validPlacement
} from "./chunk-KA7HVMDJ.js";

// src/thumb-layout.ts
function defaultControllerLayout(w, h, keys) {
  const result = {};
  const has = (key) => keys.includes(key);
  if (w < 200 || h < 160) return result;
  const box = (key, x, y, width, height, shape = "rounded") => {
    result[key] = { x: x + width / 2, y: y + height / 2, size: Math.min(width, height), shape, zone: { x, y, width, height } };
  };
  const circle = (key, x, y, size) => box(key, x - size / 2, y - size / 2, size, size, "circle");
  const faceKeys = ["A", "B", "X", "Y"].filter(has);
  const actionShoulders = !has("joyR") && (has("joyL") || keys.some((k) => k.startsWith("dpad"))) && faceKeys.length && faceKeys.length + ["R1", "R2"].filter(has).length <= 4 ? ["R1", "R2"].filter(has) : [];
  const utility = ["L2", "L1", ...has("joyL") ? ["dpadU", "dpadL", "dpadR", "dpadD"] : [], "select", "start", "R1", "R2"].filter((key) => has(key) && !actionShoulders.includes(key));
  const columns = Math.max(1, Math.min(utility.length, Math.floor((w - 8) / 52)));
  const rows = Math.ceil(utility.length / columns), cell = Math.min(80, (w - 8) / columns), uw = cell * columns;
  utility.forEach((key, i) => box(key, (w - uw) / 2 + i % columns * cell + 2, Math.floor(i / columns) * 52 + 4, cell - 4, 48));
  const top = rows * 52 + (rows === 0 ? 8 : 12), available = h - top - 16;
  const hasLeft = has("joyL") || keys.some((k) => k.startsWith("dpad")), faces = ["A", "B", "X", "Y"].filter(has);
  const leftX = Math.max(96, Math.min(116, w * 0.24)), rightX = w - leftX;
  const diameter = Math.min(152, w * 0.4, available * 0.78), thumbY = Math.min(h - diameter / 2 - 16, h - Math.min(112, available * 0.4));
  if (!hasLeft && !has("joyR") && faces.length) {
    const cols = faces.length === 1 ? 1 : 2, count = Math.ceil(faces.length / cols);
    const bw = Math.min(180, (w - 40) / cols), bh = Math.min(100, (available - (count - 1) * 12) / count), y = h - 16 - count * bh - (count - 1) * 12;
    faces.forEach((key, i) => box(key, cols === 1 ? (w - bw) / 2 : i % 2 === 0 ? 16 : w - bw - 16, y + Math.floor(i / cols) * (bh + 12), bw, bh));
  } else if (faces.length && !has("joyR")) {
    const actions = [...faces, ...actionShoulders], secondary = actions.slice(1);
    const cols = w >= 560 && available < 200 ? 3 : 2;
    const count = Math.ceil(secondary.length / cols), gap = 10, bw = Math.min(cols === 3 ? 184 : 160, w * 0.5 - 32);
    const size = Math.max(48, Math.min(64, (bw - (cols - 1) * gap) / cols, count ? (available - 48 - count * gap) / count : 64));
    const bh = Math.min(80, available - count * (size + gap)), x = w - 16 - bw, y = h - 16 - bh;
    box(actions[0], x, y, bw, bh);
    secondary.forEach((key, i) => box(key, w - 16 - size - i % cols * (size + gap), y - (Math.floor(i / cols) + 1) * (size + gap), size, size));
  } else if (faces.length) {
    const dual = has("joyR"), inboard = dual && w >= 560;
    const cx = inboard ? rightX - 184 : rightX + (!has("joyL") && hasLeft ? Math.min(16, w * 0.04) : 0);
    const cy = dual && !inboard ? thumbY - diameter / 2 - 100 : thumbY;
    const space = dual && !inboard ? Math.max(96, available - diameter - 20) : available;
    if (faces.length === 1) circle(faces[0], cx, Math.max(top + 44, cy), Math.min(92, space * 0.7));
    else if (faces.length <= 2) {
      const size = Math.min(76, space * 0.4, w * 0.19);
      circle(faces[0], cx + size * 0.46, Math.max(top + size / 2, cy + size * 0.42), size);
      circle(faces[1], cx - size * 0.58, Math.max(top + size / 2, cy - size * 0.58), size * 0.88);
    } else {
      const size = Math.min(64, Math.max(48, Math.min(w * 0.16, space / 2.9))), gap = size * 0.88;
      const centreY = Math.min(h - size / 2 - gap - 16, Math.max(top + size / 2 + gap, cy));
      if (space >= size + gap * 2) {
        for (const [key, dx, dy] of [["A", 0, 1], ["B", 1, 0], ["X", -1, 0], ["Y", 0, -1]]) if (has(key)) circle(key, cx + dx * gap, centreY + dy * gap, size);
      } else {
        const d = Math.min(56, (space - 8) / 2);
        faces.forEach((key, i) => circle(key, cx + (i % 2 === 0 ? -1 : 1) * (d + 8) / 2, top + space - d / 2 - Math.floor(i / 2) * (d + 8), d));
      }
    }
  }
  if (!has("joyL")) {
    const size = Math.min(56, Math.max(48, (w * 0.5 - 32) / 3), available / 3), cy = Math.min(h - size * 1.5 - 12, Math.max(top + size * 1.5, thumbY));
    for (const [key, dx, dy] of [["dpadU", 0, -1], ["dpadD", 0, 1], ["dpadL", -1, 0], ["dpadR", 1, 0]]) if (has(key)) box(key, leftX + dx * size - size / 2, cy + dy * size - size / 2, size, size);
  }
  for (const key of ["joyL", "joyR"]) if (has(key)) {
    const y = Math.max(top, Math.min(h * 0.34, h - 180));
    const zone = { x: key === "joyL" ? 4 : w * 0.51, y, width: w * 0.49 - 4, height: h - y - 12 };
    result[key] = { x: key === "joyL" ? leftX : rightX, y: thumbY, size: diameter, shape: "circle", zone };
  }
  return result;
}

// src/nostr-profile.ts
var CONTROLLER_PROFILE_KIND_DRAFT = 30393;
function controllerProfileFromEvent(event, publisher, gameId, verifySignature, kind = CONTROLLER_PROFILE_KIND_DRAFT) {
  try {
    if (!/^[a-f0-9]{64}$/.test(publisher) || event.pubkey !== publisher || event.kind !== kind || !Number.isInteger(event.created_at) || event.created_at < 0 || event.content.length > 49152 || !/^[a-f0-9]{64}$/.test(event.id) || !/^[a-f0-9]{128}$/.test(event.sig) || !/^[a-z0-9][a-z0-9._-]{0,127}$/.test(gameId) || !Array.isArray(event.tags) || event.tags.length > 32) return null;
    const d = event.tags.filter((tag) => tag[0] === "d"), protocol = event.tags.filter((tag) => tag[0] === "protocol");
    if (d.length !== 1 || d[0]?.length !== 2 || d[0][1] !== gameId || protocol.length !== 1 || protocol[0]?.length !== 3 || protocol[0][1] !== "nostr-game-controller" || protocol[0][2] !== "1") return null;
    if (verifySignature(event) !== true) return null;
    const spec = JSON.parse(event.content);
    return isControllerSpec(spec) ? spec : null;
  } catch {
    return null;
  }
}

// src/game-definition.ts
var GAME_DEFINITION_KIND = 31990;
var GAME_DEFINITION_MAX_BYTES = 65536;
var object = (value) => typeof value === "object" && value !== null && !Array.isArray(value);
function controllerProfileFromGameDefinition(value) {
  try {
    if (!object(value) || value.manifestVersion !== 2 || typeof value.gameId !== "string" || !/^[a-z0-9][a-z0-9-]{0,63}$/.test(value.gameId) || typeof value.name !== "string" || !value.name.length || value.name.length > 80 || typeof value.url !== "string" || new TextEncoder().encode(JSON.stringify(value)).length > GAME_DEFINITION_MAX_BYTES) return null;
    const url = new URL(value.url);
    if (url.protocol !== "https:" || url.username || url.password) return null;
    const controller = value.controller;
    if (!object(controller) || typeof controller.adapter !== "string" || !["native", "keyboard", "pointer", "hybrid"].includes(controller.adapter) || controller.phoneProtocol !== "gamestr-controller-v1" || !isControllerSpec(controller.profile)) return null;
    return controller.profile;
  } catch {
    return null;
  }
}
function controllerProfileFromGameDefinitionEvent(event, publisher, gameId, verifySignature) {
  try {
    const snapshot = JSON.parse(JSON.stringify(event));
    if (!/^[a-f0-9]{64}$/.test(publisher) || snapshot.pubkey !== publisher || snapshot.kind !== GAME_DEFINITION_KIND || !/^[a-z0-9][a-z0-9-]{0,63}$/.test(gameId) || !Number.isInteger(snapshot.created_at) || snapshot.created_at < 0 || !/^[a-f0-9]{64}$/.test(snapshot.id) || !/^[a-f0-9]{128}$/.test(snapshot.sig) || typeof snapshot.content !== "string" || new TextEncoder().encode(snapshot.content).length > GAME_DEFINITION_MAX_BYTES || !Array.isArray(snapshot.tags) || snapshot.tags.length > 64 || snapshot.tags.some((tag) => !Array.isArray(tag) || tag.some((value) => typeof value !== "string"))) return null;
    const d = snapshot.tags.filter((tag) => tag[0] === "d");
    if (d.length !== 1 || d[0]?.length !== 2 || d[0][1] !== `gamestr-game:${gameId}` || !snapshot.tags.some((tag) => tag.length === 2 && tag[0] === "t" && tag[1] === "gamestr-game")) return null;
    if (verifySignature(JSON.parse(JSON.stringify(snapshot))) !== true) return null;
    const metadata = JSON.parse(snapshot.content);
    if (!object(metadata) || !object(metadata.gamestr_manifest) || metadata.gamestr_manifest.gameId !== gameId) return null;
    const definition = metadata.gamestr_manifest;
    if (metadata.website !== void 0 && metadata.website !== definition.url) return null;
    const scoreKind = definition.scoreKind ?? 30762;
    if (scoreKind !== 30762 && scoreKind !== 5555 || !snapshot.tags.some((tag) => tag.length === 2 && tag[0] === "k" && tag[1] === String(scoreKind)) || !snapshot.tags.some((tag) => tag.length === 2 && tag[0] === "web" && tag[1] === definition.url)) return null;
    return controllerProfileFromGameDefinition(definition);
  } catch {
    return null;
  }
}

// src/game-listing.ts
var GAME_LISTING_KIND = 30761;
var GAME_LISTING_MAX_EVENT_BYTES = 65536;
var GAME_SCORE_KINDS = [30762, 5555];
var GAME_INPUT_METHODS = ["keyboard", "gamepad", "pointer", "touch", "phone"];
var GAME_PLATFORMS = ["web", "windows", "macos", "linux", "android", "ios"];
var GAME_ORIGIN_PROOF_PATH = "/.well-known/gamestr.json";
var STICKS = /* @__PURE__ */ new Set(["joyL", "joyR"]);
var HEX64 = /^[0-9a-f]{64}$/;
var ACTION_ID = /^[a-z][a-z0-9-]{0,31}$/;
var KEY_CODE = /^[A-Za-z][A-Za-z0-9]{0,31}$/;
var TAG_NAME = /^[^\s\u0000-\u001f\u007f]{1,64}$/;
var object2 = (value) => typeof value === "object" && value !== null && !Array.isArray(value);
var text = (value, max, min = 1) => typeof value === "string" && value.length >= min && value.length <= max && !/[\u0000-\u001f\u007f]/.test(value);
var integer = (value, min, max) => Number.isInteger(value) && value >= min && value <= max;
function httpsUrl(value) {
  if (!text(value, 2048)) return false;
  try {
    const url = new URL(value);
    return url.protocol === "https:" && !url.username && !url.password;
  } catch {
    return false;
  }
}
var isGameIdentifier = (value) => text(value, 64) && !value.includes(":") && value.trim() === value;
function checkLeaderboard(board, at, errors) {
  if (!object2(board)) {
    errors.push(`${at} must be an object`);
    return;
  }
  if (!text(board.label, 64)) errors.push(`${at}.label must be 1-64 characters`);
  for (const key of ["scoreTag", "displayTag", "filterTag"])
    if ((key === "scoreTag" || board[key] !== void 0) && !(typeof board[key] === "string" && TAG_NAME.test(board[key])))
      errors.push(`${at}.${key} must be a tag name`);
  if (board.direction !== "asc" && board.direction !== "desc") errors.push(`${at}.direction must be asc or desc`);
  for (const key of ["displayLabel", "filterValue"])
    if (board[key] !== void 0 && !text(board[key], 128)) errors.push(`${at}.${key} must be 1-128 characters`);
  if (board.filterTag === void 0 !== (board.filterValue === void 0)) errors.push(`${at} needs both filterTag and filterValue`);
  if (board.scoreFormat !== void 0 && board.scoreFormat !== "number" && board.scoreFormat !== "time") errors.push(`${at}.scoreFormat must be number or time`);
  if (board.scoreUnit !== void 0 && board.scoreUnit !== "ms" && board.scoreUnit !== "s") errors.push(`${at}.scoreUnit must be ms or s`);
  if (board.deduplicateByPlayer !== void 0 && typeof board.deduplicateByPlayer !== "boolean" && board.deduplicateByPlayer !== "best" && board.deduplicateByPlayer !== "latest") errors.push(`${at}.deduplicateByPlayer must be a boolean, best or latest`);
}
function checkScores(scores, errors) {
  if (!object2(scores)) {
    errors.push("scores must be an object");
    return;
  }
  if (scores.signer !== void 0 && scores.signer !== "developer" && scores.signer !== "player") errors.push("scores.signer must be developer or player");
  if (scores.kinds !== void 0 && !(Array.isArray(scores.kinds) && scores.kinds.length >= 1 && scores.kinds.every((kind) => GAME_SCORE_KINDS.includes(kind)) && new Set(scores.kinds).size === scores.kinds.length))
    errors.push("scores.kinds must list distinct supported score kinds");
  if (scores.identifiers !== void 0 && !(Array.isArray(scores.identifiers) && scores.identifiers.length <= 8 && scores.identifiers.every(isGameIdentifier) && new Set(scores.identifiers).size === scores.identifiers.length))
    errors.push("scores.identifiers must be at most 8 distinct game identifiers");
  if (scores.authorities !== void 0 && !(Array.isArray(scores.authorities) && scores.authorities.length >= 1 && scores.authorities.length <= 8 && scores.authorities.every((key) => typeof key === "string" && HEX64.test(key)) && new Set(scores.authorities).size === scores.authorities.length))
    errors.push("scores.authorities must be 1-8 distinct hex public keys");
  if (scores.signer === "player" && scores.authorities !== void 0) errors.push("player-signed scores have no score authorities");
  if (scores.leaderboards !== void 0) {
    if (!Array.isArray(scores.leaderboards) || scores.leaderboards.length < 1 || scores.leaderboards.length > 16)
      errors.push("scores.leaderboards must list 1-16 boards");
    else scores.leaderboards.forEach((board, index) => checkLeaderboard(board, `scores.leaderboards[${index}]`, errors));
  }
  const split = scores.leaderboardSplit;
  if (split === void 0) return;
  if (!object2(split)) {
    errors.push("scores.leaderboardSplit must be an object");
    return;
  }
  if (!(typeof split.splitTag === "string" && TAG_NAME.test(split.splitTag))) errors.push("scores.leaderboardSplit.splitTag must be a tag name");
  if (split.scoreTag !== void 0 && !(typeof split.scoreTag === "string" && TAG_NAME.test(split.scoreTag))) errors.push("scores.leaderboardSplit.scoreTag must be a tag name");
  if (split.direction !== "asc" && split.direction !== "desc") errors.push("scores.leaderboardSplit.direction must be asc or desc");
  if (split.scoreFormat !== void 0 && split.scoreFormat !== "number" && split.scoreFormat !== "time") errors.push("scores.leaderboardSplit.scoreFormat must be number or time");
  if (split.scoreUnit !== void 0 && split.scoreUnit !== "ms" && split.scoreUnit !== "s") errors.push("scores.leaderboardSplit.scoreUnit must be ms or s");
  if (split.layout !== void 0 && split.layout !== "tabs" && split.layout !== "grid") errors.push("scores.leaderboardSplit.layout must be tabs or grid");
  if (!(Array.isArray(split.values) && split.values.length >= 1 && split.values.length <= 32 && split.values.every((entry) => object2(entry) && text(entry.value, 128) && text(entry.label, 64)) && new Set(split.values.map((entry) => entry.value)).size === split.values.length))
    errors.push("scores.leaderboardSplit.values must list 1-32 distinct value/label pairs");
}
var codes = (value) => Array.isArray(value) && value.length >= 1 && value.length <= 8 && value.every((code) => typeof code === "string" && KEY_CODE.test(code));
function checkKeyboard(action, binding) {
  if (action.type === "button") return codes(binding);
  const sides = action.type === "axis1" ? ["negative", "positive"] : ["up", "down", "left", "right"];
  return object2(binding) && Object.keys(binding).length > 0 && Object.entries(binding).every(([side, value]) => sides.includes(side) && codes(value));
}
function checkGamepad(action, bindings) {
  if (!Array.isArray(bindings) || bindings.length < 1 || bindings.length > 8) return false;
  return bindings.every((binding) => {
    if (!object2(binding)) return false;
    const keys = Object.keys(binding).length;
    if ("stick" in binding) {
      if (binding.stick !== "left" && binding.stick !== "right") return false;
      if (keys === 2) return ["up", "down", "left", "right"].includes(binding.direction) && action.type === "button";
      return keys === 1 && action.type === "axis2";
    }
    if (keys !== 1) return false;
    if ("button" in binding) return integer(binding.button, 0, 16) && action.type !== "axis2";
    if ("axis" in binding) return integer(binding.axis, 0, 3) && action.type === "axis1";
    return binding.dpad === true && action.type === "axis2";
  });
}
function checkInput(input, errors) {
  if (!object2(input)) {
    errors.push("input must be an object");
    return;
  }
  const methods = input.methods;
  if (!(Array.isArray(methods) && methods.length >= 1 && new Set(methods).size === methods.length && methods.every((method) => GAME_INPUT_METHODS.includes(method)))) {
    errors.push("input.methods must list distinct supported input methods");
    return;
  }
  const actions = /* @__PURE__ */ new Map();
  if (input.actions !== void 0) {
    if (!object2(input.actions) || Object.keys(input.actions).length < 1 || Object.keys(input.actions).length > 32)
      errors.push("input.actions must declare 1-32 actions");
    else for (const [id, action] of Object.entries(input.actions)) {
      if (!ACTION_ID.test(id)) errors.push(`input.actions.${id} has an invalid action id`);
      else if (!object2(action) || !["button", "axis1", "axis2"].includes(action.type) || !text(action.label, 48) || action.role !== void 0 && !["menu", "back", "confirm"].includes(action.role))
        errors.push(`input.actions.${id} needs a type, a 1-48 character label and an optional known role`);
      else actions.set(id, action);
    }
  }
  for (const method of ["keyboard", "gamepad"]) {
    const section = input[method];
    if (section === void 0) continue;
    if (!methods.includes(method)) errors.push(`input.${method} requires ${method} in input.methods`);
    if (!object2(section)) {
      errors.push(`input.${method} must be an object`);
      continue;
    }
    for (const [id, binding] of Object.entries(section)) {
      const action = actions.get(id);
      if (!action) errors.push(`input.${method}.${id} binds an undeclared action`);
      else if (!(method === "keyboard" ? checkKeyboard(action, binding) : checkGamepad(action, binding)))
        errors.push(`input.${method}.${id} does not suit a ${action.type} action`);
    }
  }
  if (input.phone !== void 0) {
    const phone = input.phone;
    if (!methods.includes("phone")) errors.push("input.phone requires phone in input.methods");
    if (!object2(phone) || !isControllerSpec(phone.profile)) errors.push("input.phone.profile must be a valid controller profile");
    else if (phone.bind !== void 0) {
      if (!object2(phone.bind)) errors.push("input.phone.bind must be an object");
      else for (const [slot, id] of Object.entries(phone.bind)) {
        const action = typeof id === "string" ? actions.get(id) : void 0;
        if (!(slot in phone.profile.slots)) errors.push(`input.phone.bind.${slot} is not a profile slot`);
        else if (!action) errors.push(`input.phone.bind.${slot} binds an undeclared action`);
        else if (action.type !== (STICKS.has(slot) ? "axis2" : "button")) errors.push(`input.phone.bind.${slot} does not suit a ${action.type} action`);
      }
    }
  }
  if (input.hints !== void 0 && !(Array.isArray(input.hints) && input.hints.length <= 8 && input.hints.every((hint) => text(hint, 80))))
    errors.push("input.hints must be at most 8 lines of 1-80 characters");
}
function checkGameListing(value) {
  const errors = [];
  try {
    if (!object2(value)) return { ok: false, errors: ["listing must be an object"] };
    if (value.listingVersion !== 1) return { ok: false, errors: ["listingVersion must be 1"] };
    if (!isGameIdentifier(value.gameId)) errors.push("gameId must be 1-64 characters without colons or surrounding spaces");
    if (!text(value.name, 80)) errors.push("name must be 1-80 characters");
    if (!(typeof value.description === "string" && value.description.length >= 1 && value.description.length <= 1200))
      errors.push("description must be 1-1200 characters");
    if (!httpsUrl(value.image)) errors.push("image must be an https URL");
    if (!httpsUrl(value.url)) errors.push("url must be an https URL");
    if (!(Array.isArray(value.genres) && value.genres.length >= 1 && value.genres.length <= 8 && value.genres.every((genre) => text(genre, 32)) && new Set(value.genres).size === value.genres.length))
      errors.push("genres must list 1-8 distinct genres");
    if (value.tagline !== void 0 && !text(value.tagline, 140)) errors.push("tagline must be 1-140 characters");
    if (value.banner !== void 0 && !httpsUrl(value.banner)) errors.push("banner must be an https URL");
    if (value.developer !== void 0 && !text(value.developer, 100)) errors.push("developer must be 1-100 characters");
    if (value.lud16 !== void 0 && !(text(value.lud16, 320) && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value.lud16)))
      errors.push("lud16 must be a Lightning address");
    if (value.players !== void 0 && !(object2(value.players) && integer(value.players.min, 1, 64) && integer(value.players.max, 1, 64) && value.players.min <= value.players.max))
      errors.push("players must have 1 <= min <= max <= 64");
    if (value.network !== void 0 && !["required", "optional", "offline"].includes(value.network))
      errors.push("network must be required, optional or offline");
    if (value.ageRating !== void 0 && !text(value.ageRating, 32)) errors.push("ageRating must be 1-32 characters");
    if (value.capabilities !== void 0 && !(object2(value.capabilities) && Object.entries(value.capabilities).every(([key, flag]) => ["nostrSign", "walletPay", "persistentStorage", "externalNavigation"].includes(key) && typeof flag === "boolean")))
      errors.push("capabilities must contain known boolean requests");
    if (value.platforms !== void 0 && !(Array.isArray(value.platforms) && value.platforms.length >= 1 && value.platforms.length <= 6 && value.platforms.every((entry) => object2(entry) && GAME_PLATFORMS.includes(entry.platform) && (entry.url === void 0 || httpsUrl(entry.url))) && new Set(value.platforms.map((entry) => entry.platform)).size === value.platforms.length))
      errors.push("platforms must list 1-6 distinct platforms with optional https URLs");
    if (value.achievements !== void 0 && !(Array.isArray(value.achievements) && value.achievements.length >= 1 && value.achievements.length <= 64 && value.achievements.every((entry) => object2(entry) && typeof entry.id === "string" && /^[^\s,\u0000-\u001f\u007f]{1,64}$/.test(entry.id) && text(entry.name, 64) && (entry.description === void 0 || text(entry.description, 280)) && (entry.image === void 0 || httpsUrl(entry.image)) && (entry.badge === void 0 || typeof entry.badge === "string" && /^30009:[0-9a-f]{64}:[^:]{1,64}$/.test(entry.badge))) && new Set(value.achievements.map((entry) => entry.id)).size === value.achievements.length))
      errors.push("achievements must list 1-64 distinct ids with names");
    if (value.orientation !== void 0 && !["landscape", "portrait", "any"].includes(value.orientation))
      errors.push("orientation must be landscape, portrait or any");
    if (value.embeddable !== void 0 && typeof value.embeddable !== "boolean") errors.push("embeddable must be a boolean");
    if (value.license !== void 0 && !(typeof value.license === "string" && /^[A-Za-z0-9.+()\- ]{1,64}$/.test(value.license)))
      errors.push("license must be an SPDX licence expression");
    if (value.repository !== void 0 && !httpsUrl(value.repository)) errors.push("repository must be an https URL");
    if (value.scores !== void 0) checkScores(value.scores, errors);
    if (value.input !== void 0) checkInput(value.input, errors);
  } catch {
    errors.push("listing could not be read");
  }
  return errors.length ? { ok: false, errors } : { ok: true, listing: value };
}
function gameListingFromEvent(event, verifySignature) {
  try {
    const serialised = JSON.stringify(event);
    if (new TextEncoder().encode(serialised).length > GAME_LISTING_MAX_EVENT_BYTES) return null;
    const snapshot = JSON.parse(serialised);
    if (snapshot.kind !== GAME_LISTING_KIND || !HEX64.test(snapshot.pubkey) || !HEX64.test(snapshot.id) || !/^[0-9a-f]{128}$/.test(snapshot.sig) || !Number.isInteger(snapshot.created_at) || snapshot.created_at < 0 || typeof snapshot.content !== "string" || !Array.isArray(snapshot.tags) || snapshot.tags.length > 64 || snapshot.tags.some((tag) => !Array.isArray(tag) || tag.some((value) => typeof value !== "string"))) return null;
    const d = snapshot.tags.filter((tag) => tag[0] === "d");
    const gameId = d.length === 1 && d[0]?.length === 2 ? d[0][1] : void 0;
    if (!isGameIdentifier(gameId)) return null;
    if (verifySignature(JSON.parse(serialised)) !== true) return null;
    const checked = checkGameListing(JSON.parse(snapshot.content));
    if (!checked.ok || checked.listing.gameId !== gameId) return null;
    const listing = checked.listing;
    return { publisher: snapshot.pubkey, gameId: listing.gameId, createdAt: snapshot.created_at, listing };
  } catch {
    return null;
  }
}
function unboundGameActions(listing) {
  const input = listing.input;
  const ids = Object.keys(input?.actions ?? {});
  if (!input || !ids.length) return {};
  const result = {};
  if (input.methods.includes("keyboard")) result.keyboard = ids.filter((id) => !input.keyboard?.[id]);
  if (input.methods.includes("gamepad")) result.gamepad = ids.filter((id) => !input.gamepad?.[id]);
  if (input.phone) {
    const bound = new Set(Object.values(input.phone.bind ?? {}));
    result.phone = ids.filter((id) => !bound.has(id));
  }
  return result;
}
function gameOriginProofUrl(listing) {
  return new URL(GAME_ORIGIN_PROOF_PATH, listing.url).href;
}
function gameOriginProofMatches(document, gameId, publisher) {
  if (!object2(document) || !object2(document.games) || !HEX64.test(publisher)) return false;
  const keys = Object.prototype.hasOwnProperty.call(document.games, gameId) ? document.games[gameId] : void 0;
  return Array.isArray(keys) && keys.length <= 8 && keys.includes(publisher);
}
function controllerSpecFromListing(listing) {
  return listing.input?.phone?.profile ?? null;
}
function phoneSlotAction(listing, slot) {
  const bind = listing.input?.phone?.bind;
  return bind && Object.prototype.hasOwnProperty.call(bind, slot) ? bind[slot] : void 0;
}

// src/vocabulary.json
var vocabulary_default = {
  version: 1,
  games: [
    "PALLASITE",
    "TANK",
    "TANK ROYALE",
    "QUIZ",
    "QUIZ BUZZ",
    "TILT",
    "TILT RACE",
    "Tank Royale",
    "Quiz Buzz",
    "Tilt Race",
    "GAME",
    "Gamestr Arcade",
    "Gamestr game",
    "Hang On, Fren",
    "Pallasite",
    "Take Me To Your Ledger",
    "Neon Sentinel"
  ],
  labels: [
    "",
    "FIRE",
    "SHIELD",
    "WARP",
    "PAUSE",
    "SELECT",
    "BACK",
    "CHEER",
    "TILT",
    "BOOST",
    "OPT A",
    "OPT B",
    "OPT X",
    "OPT Y",
    "MOVE",
    "STEER",
    "JUMP",
    "ACTION",
    "START",
    "MENU",
    "OK",
    "UP",
    "DOWN",
    "LEFT",
    "RIGHT",
    "BRAKE",
    "GO",
    "SHOOT",
    "USE",
    "DASH",
    "BLOCK",
    "SPECIAL",
    "Browse",
    "Choose",
    "Back",
    "Move",
    "Aim",
    "Pause",
    "View",
    "Steer",
    "Brake / Begin",
    "Continue",
    "Aim / fly",
    "Fire",
    "Shield",
    "Warp",
    "Thrust",
    "Fly",
    "Beam",
    "Special",
    "Pause / begin",
    "Burst"
  ],
  icons: [
    "\u25CF",
    "\u26E8",
    "\u26A1",
    "\u23F8",
    "\u2713",
    "\xB7",
    "\u21A9",
    "\u25B2",
    "\u25BC",
    "\u25C0",
    "\u25B6",
    "\u23E9",
    "\u{1F44F}",
    "A",
    "B",
    "X",
    "Y",
    "\u2190",
    "\u25C9",
    "\u03DF",
    "\u2191",
    "\u2161",
    "\u2193"
  ],
  phases: ["WAITING", "STARTING", "ROUND LIVE", "BETWEEN ROUNDS", "RACE LIVE", "BETWEEN RACES"],
  feedback: [
    "",
    "Press an answer button to start",
    "Get ready\u2026",
    "Choose your answer",
    "ANSWER {slot} LOCKED",
    "CORRECT \xB7 +{n} points",
    "INCORRECT",
    "NO ANSWER",
    "GAME COMPLETE \xB7 {n} points",
    "GAME PAUSED",
    "GAME RESUMED"
  ],
  scoreLabels: ["1st \xB7 {n} right", "{n} right", "tank round win", "1st!", "2nd", "3rd", "4th"],
  highlights: ["cheered!", "took the round"],
  graphics: [
    { name: "tank-fire", sha256: "05f14b1dc820b0f0020899b310708abc6ed674cef9119a779328fac7d35e6b45" }
  ]
};

// src/vocabulary.ts
var VOCABULARY = vocabulary_default;
var INITIALS_RE = /^[A-Z]{3}$/;
var isInitials = (value) => typeof value === "string" && INITIALS_RE.test(value);
var COLOUR_RE = /^(?:#[0-9a-fA-F]{3}(?:[0-9a-fA-F]{3})?|rgba\((?:25[0-5]|2[0-4]\d|1?\d?\d), ?(?:25[0-5]|2[0-4]\d|1?\d?\d), ?(?:25[0-5]|2[0-4]\d|1?\d?\d), ?(?:0|1|0\.\d{1,3})\))$/;
var SCORE_MAX = 1e6;
var SLOTS = ["joyL", "joyR", "A", "B", "X", "Y", "L1", "L2", "R1", "R2", "start", "select", "dpadU", "dpadD", "dpadL", "dpadR"];
var FEEDBACK_SLOTS = ["A", "B", "X", "Y"];
var escape = (text2) => text2.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
var alternation = (items) => items.map(escape).join("|");
var N = "(0|[1-9][0-9]{0,6})";
function template(text2) {
  const pattern = new RegExp("^" + escape(text2).replace(/\\\{n\\\}/g, N).replace(/\\\{slot\\\}/g, `(${FEEDBACK_SLOTS.join("|")})`) + "$");
  const counted = text2.includes("{n}");
  return (value) => {
    const match = pattern.exec(value);
    if (!match) return false;
    return !counted || match.slice(1).every((part) => !/^\d+$/.test(part) || Number(part) <= SCORE_MAX);
  };
}
function sha256Hex(bytes) {
  const K = new Uint32Array([
    1116352408,
    1899447441,
    3049323471,
    3921009573,
    961987163,
    1508970993,
    2453635748,
    2870763221,
    3624381080,
    310598401,
    607225278,
    1426881987,
    1925078388,
    2162078206,
    2614888103,
    3248222580,
    3835390401,
    4022224774,
    264347078,
    604807628,
    770255983,
    1249150122,
    1555081692,
    1996064986,
    2554220882,
    2821834349,
    2952996808,
    3210313671,
    3336571891,
    3584528711,
    113926993,
    338241895,
    666307205,
    773529912,
    1294757372,
    1396182291,
    1695183700,
    1986661051,
    2177026350,
    2456956037,
    2730485921,
    2820302411,
    3259730800,
    3345764771,
    3516065817,
    3600352804,
    4094571909,
    275423344,
    430227734,
    506948616,
    659060556,
    883997877,
    958139571,
    1322822218,
    1537002063,
    1747873779,
    1955562222,
    2024104815,
    2227730452,
    2361852424,
    2428436474,
    2756734187,
    3204031479,
    3329325298
  ]);
  const h = new Uint32Array([1779033703, 3144134277, 1013904242, 2773480762, 1359893119, 2600822924, 528734635, 1541459225]);
  const length = bytes.length, padded = new Uint8Array(length + 9 + 63 >> 6 << 6);
  padded.set(bytes);
  padded[length] = 128;
  const view = new DataView(padded.buffer);
  view.setUint32(padded.length - 8, Math.floor(length / 536870912));
  view.setUint32(padded.length - 4, length << 3 >>> 0);
  const w = new Uint32Array(64);
  const rotr = (x, n) => x >>> n | x << 32 - n;
  for (let offset = 0; offset < padded.length; offset += 64) {
    for (let i = 0; i < 16; i++) w[i] = view.getUint32(offset + i * 4);
    for (let i = 16; i < 64; i++) {
      const s0 = rotr(w[i - 15], 7) ^ rotr(w[i - 15], 18) ^ w[i - 15] >>> 3, s1 = rotr(w[i - 2], 17) ^ rotr(w[i - 2], 19) ^ w[i - 2] >>> 10;
      w[i] = w[i - 16] + s0 + w[i - 7] + s1 >>> 0;
    }
    let [a, b, c, d, e, f, g, hh] = h;
    for (let i = 0; i < 64; i++) {
      const t1 = hh + (rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25)) + (e & f ^ ~e & g) + K[i] + w[i] >>> 0;
      const t2 = (rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22)) + (a & b ^ a & c ^ b & c) >>> 0;
      hh = g;
      g = f;
      f = e;
      e = d + t1 >>> 0;
      d = c;
      c = b;
      b = a;
      a = t1 + t2 >>> 0;
    }
    const next = [a, b, c, d, e, f, g, hh];
    for (let i = 0; i < 8; i++) h[i] = h[i] + next[i] >>> 0;
  }
  return Array.from(h, (word) => word.toString(16).padStart(8, "0")).join("");
}
function encodeBase64(bytes) {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return globalThis.btoa(binary);
}
function decodeBase64(text2) {
  try {
    const binary = globalThis.atob?.(text2);
    if (binary === void 0) return null;
    return Uint8Array.from(binary, (character) => character.charCodeAt(0));
  } catch {
    return null;
  }
}
function compileVocabulary(vocabulary = VOCABULARY) {
  const index = "(?:[1-9]|[1-5][0-9]|6[0-4])";
  const game = alternation([...vocabulary.games].sort((a, b) => b.length - a.length));
  const player = `P${index}(?: \xB7 (?:BOT|[A-Z]{3}))?`;
  const specName = new RegExp(`^(?:${game})(?: \xB7 MENU)?(?: \xB7 P${index}| \xB7 SPECTATOR ${index})?$`);
  const title = new RegExp(`^(?:${game}) \xB7 (?:${alternation(vocabulary.phases)})$`);
  const rowLabel = new RegExp(`^(?:${index}\\. )?${player}$`);
  const highlight = new RegExp(`^${player} (?:${alternation(vocabulary.highlights)})$`);
  const feedback = vocabulary.feedback.map(template), scoreLabels = vocabulary.scoreLabels.map(template);
  const labels = new Set(vocabulary.labels), icons = new Set(vocabulary.icons), graphics = new Set(vocabulary.graphics.map((item) => item.sha256));
  const string = (value) => typeof value === "string";
  return {
    vocabulary,
    specName: (value) => string(value) && specName.test(value),
    title: (value) => string(value) && title.test(value),
    rowLabel: (value) => string(value) && rowLabel.test(value),
    highlight: (value) => string(value) && highlight.test(value),
    feedback: (value) => string(value) && feedback.some((match) => match(value)),
    scoreLabel: (value) => string(value) && scoreLabels.some((match) => match(value)),
    label: (value) => string(value) && labels.has(value),
    icon: (value) => string(value) && icons.has(value),
    graphic: (value) => {
      if (typeof value !== "string" || value.length > 12288 || value.length % 4 !== 0 || !/^[A-Za-z0-9+/]+={0,2}$/.test(value)) return false;
      const bytes = decodeBase64(value);
      return !!bytes && encodeBase64(bytes) === value && graphics.has(sha256Hex(bytes));
    }
  };
}
var DEFAULT_COMPILED = compileVocabulary();
var isObject = (value) => typeof value === "object" && value !== null && !Array.isArray(value);
function canonicaliseSpec(value, compiled = DEFAULT_COMPILED) {
  if (!isObject(value) || value.version !== 1 || !isObject(value.slots)) return null;
  const stripped = [];
  const name = compiled.specName(value.name) ? value.name : "GAME";
  if (name !== value.name) stripped.push("name");
  const slots = {};
  const keys = Object.keys(value.slots).filter((key) => SLOTS.includes(key));
  if (keys.length !== Object.keys(value.slots).length) stripped.push("slots");
  for (const key of keys.slice(0, 16)) {
    const source = value.slots[key];
    if (!isObject(source)) {
      stripped.push(`slots.${key}`);
      continue;
    }
    const slot = {};
    if (source.mode !== void 0) {
      if (source.mode === "heading" || source.mode === "xy" || source.mode === "tilt") slot.mode = source.mode;
      else stripped.push(`slots.${key}.mode`);
    }
    if (source.tapAction !== void 0) {
      if (typeof source.tapAction === "string" && SLOTS.includes(source.tapAction)) slot.tapAction = source.tapAction;
      else stripped.push(`slots.${key}.tapAction`);
    }
    if (source.label !== void 0) {
      if (compiled.label(source.label)) slot.label = source.label;
      else stripped.push(`slots.${key}.label`);
    }
    if (source.icon !== void 0) {
      if (compiled.icon(source.icon)) slot.icon = source.icon;
      else stripped.push(`slots.${key}.icon`);
    }
    if (source.colour !== void 0) {
      if (typeof source.colour === "string" && COLOUR_RE.test(source.colour)) slot.colour = source.colour;
      else stripped.push(`slots.${key}.colour`);
    }
    if (source.graphic !== void 0) {
      if (isObject(source.graphic) && compiled.graphic(source.graphic.png)) slot.graphic = { png: source.graphic.png };
      else stripped.push(`slots.${key}.graphic`);
    }
    slots[key] = slot;
  }
  const spec = { version: 1, name, slots };
  if (isObject(value.theme)) {
    const theme = {};
    for (const field of ["background", "accent"]) {
      const colour = value.theme[field];
      if (colour === void 0) continue;
      if (typeof colour === "string" && COLOUR_RE.test(colour)) theme[field] = colour;
      else stripped.push(`theme.${field}`);
    }
    spec.theme = theme;
  } else if (value.theme !== void 0) stripped.push("theme");
  if (value.layout !== void 0) {
    const layout = canonicaliseLayout(value.layout, Object.keys(slots));
    if (layout) spec.layout = layout;
    else stripped.push("layout");
  }
  return { spec, stripped };
}
var round3 = (number) => Math.round(number * 1e3) / 1e3;
function canonicaliseLayout(value, keys) {
  if (!isObject(value) || value.version !== 1 || value.portrait === void 0 && value.landscape === void 0) return null;
  const layout = { version: 1 };
  for (const orientation of ["portrait", "landscape"]) {
    const map = value[orientation];
    if (map === void 0) continue;
    if (!isObject(map) || Object.keys(map).length !== keys.length) return null;
    const rebuilt = {};
    for (const key of keys) {
      const placement = map[key];
      if (!validPlacement(placement, key.startsWith("joy"))) return null;
      const r = round3;
      rebuilt[key] = {
        x: r(placement.x),
        y: r(placement.y),
        size: r(placement.size),
        ...placement.shape !== void 0 ? { shape: placement.shape } : {},
        ...placement.zone !== void 0 ? { zone: { x: r(placement.zone.x), y: r(placement.zone.y), width: r(placement.zone.width), height: r(placement.zone.height) } } : {}
      };
    }
    layout[orientation] = rebuilt;
  }
  return layout;
}

// src/index.ts
var CONTROLLER_WS_ENDPOINT_DEFAULT = "wss://controller.pallasite.app/";
function formatPairingCode(code) {
  return /^[a-z0-9]{6}$/i.test(code) ? `${code.slice(0, 3)} ${code.slice(3)}` : code;
}
function normalizePairingCode(input) {
  const value = input.trim();
  return /^[a-z0-9]{3}[\s-]+[a-z0-9]{3}$/i.test(value) ? value.replace(/[\s-]/g, "").toUpperCase() : value.toUpperCase();
}
export {
  COLOUR_RE,
  CONTROLLER_PROFILE_KIND_DRAFT,
  CONTROLLER_WS_ENDPOINT_DEFAULT,
  GAME_DEFINITION_KIND,
  GAME_DEFINITION_MAX_BYTES,
  GAME_INPUT_METHODS,
  GAME_LISTING_KIND,
  GAME_LISTING_MAX_EVENT_BYTES,
  GAME_ORIGIN_PROOF_PATH,
  GAME_PLATFORMS,
  GAME_SCORE_KINDS,
  INITIALS_RE,
  SCORE_MAX,
  VOCABULARY,
  canonicaliseSpec,
  checkGameListing,
  compileVocabulary,
  controllerProfileFromEvent,
  controllerProfileFromGameDefinition,
  controllerProfileFromGameDefinitionEvent,
  controllerSpecFromListing,
  defaultControllerLayout,
  formatPairingCode,
  gameListingFromEvent,
  gameOriginProofMatches,
  gameOriginProofUrl,
  isControllerSpec,
  isGameIdentifier,
  isInitials,
  normalizePairingCode,
  phoneSlotAction,
  resolveControllerLayout,
  resolvePlacement,
  sha256Hex,
  unboundGameActions,
  validGraphic,
  validPlacement
};
//# sourceMappingURL=index.js.map